export type VehicleNotesFormState = Readonly<{
  partsLaborWarrantyMonths: string;
  partsLaborWarrantyMiles: string;
  isVehicleModified: string;
  tireType: string;
  tireSectionWidthMm: string;
  tireAspectRatio: string;
  tireWheelDiameterIn: string;
  oemTireSectionWidthMm: string;
  oemTireAspectRatio: string;
  oemTireWheelDiameterIn: string;
  hasLiftKit: string;
  liftAmountInches: string;
  hasEngineModifications: string;
  engineModificationDescription: string;
  coolantLevel: string;
  coolantCondition: string;
  engineOilLevel: string;
  engineOilCondition: string;
  transmissionFluidLevel: string;
  transmissionFluidCondition: string;
  hasLeaks: string;
  leakLocation: string;
  leakDripCondition: string;
  hasWarningLight: string;
  codesAndDefinitions: string;
}>;

/** Payload shape at root level alongside `vehicle_arrival_method`. */
export type VehicleNotesPayload = Readonly<{
  parts_labor_warranty_months: string;
  parts_labor_warranty_miles: string;
  is_vehicle_modified: string;
  tire_type: string;
  tire_section_width_mm: string;
  tire_aspect_ratio: string;
  tire_wheel_diameter_in: string;
  oem_tire_section_width_mm: string;
  oem_tire_aspect_ratio: string;
  oem_tire_wheel_diameter_in: string;
  has_lift_kit: string;
  lift_amount_inches: string;
  has_engine_modifications: string;
  engine_modification_description: string;
  coolant_level: string;
  coolant_condition: string;
  engine_oil_level: string;
  engine_oil_condition: string;
  transmission_fluid_level: string;
  transmission_fluid_condition: string;
  has_leaks: string;
  leak_location: string;
  leak_drip_condition: string;
  has_warning_light: string;
  codes_and_definitions: string;
}>;

export const TIRE_TYPE_SELECT_OPTIONS = [
  { key: 'P', label: 'P — Passenger' },
  { key: 'LT', label: 'LT — Light Truck' },
  { key: 'EURO', label: '(none / Euro-metric)' },
  { key: 'T', label: 'T — Temporary' },
  { key: 'ST', label: 'ST — Special Trailer' },
] as const;

export const FLUID_LEVEL_OPTIONS = [
  { value: 'full', label: 'Full' },
  { value: 'low', label: 'Low' },
  { value: 'na', label: 'N/A' },
] as const;

export const LEAK_DRIP_OPTIONS = [
  { value: 'drip', label: 'Leaking to a drip' },
  { value: 'seepage', label: 'Seepage' },
] as const;

export const INITIAL_VEHICLE_NOTES: VehicleNotesFormState = {
  partsLaborWarrantyMonths: '',
  partsLaborWarrantyMiles: '',
  isVehicleModified: 'no',
  tireType: '',
  tireSectionWidthMm: '',
  tireAspectRatio: '',
  tireWheelDiameterIn: '',
  oemTireSectionWidthMm: '',
  oemTireAspectRatio: '',
  oemTireWheelDiameterIn: '',
  hasLiftKit: '',
  liftAmountInches: '',
  hasEngineModifications: '',
  engineModificationDescription: '',
  coolantLevel: '',
  coolantCondition: '',
  engineOilLevel: '',
  engineOilCondition: '',
  transmissionFluidLevel: '',
  transmissionFluidCondition: '',
  hasLeaks: 'no',
  leakLocation: '',
  leakDripCondition: '',
  hasWarningLight: 'no',
  codesAndDefinitions: '',
};

export const VEHICLE_MODIFICATION_CLEAR_PATCH: Partial<VehicleNotesFormState> = {
  tireType: '',
  tireSectionWidthMm: '',
  tireAspectRatio: '',
  tireWheelDiameterIn: '',
  oemTireSectionWidthMm: '',
  oemTireAspectRatio: '',
  oemTireWheelDiameterIn: '',
  hasLiftKit: '',
  liftAmountInches: '',
  hasEngineModifications: '',
  engineModificationDescription: '',
};

export function tireTypeToSelectKey(tireType: string): string {
  return tireType.trim();
}

export function tireTypeFromSelectKey(key: string): string {
  return key.trim();
}

/** Short type letter used in captured tire size notation (e.g. LT265/70R17). */
function tireTypeNotationPrefix(tireType: string): string {
  const normalized = tireType.trim();
  if (!normalized || normalized === 'EURO') return '';
  return normalized;
}

export function tireTypeToPayloadValue(tireType: string): string {
  const normalized = tireType.trim();
  if (!normalized) return '';
  const match = TIRE_TYPE_SELECT_OPTIONS.find((opt) => opt.key === normalized);
  return match?.label ?? normalized;
}

export function formatTireSizeFromParts(
  width: string,
  aspect: string,
  diameter: string,
  type?: string
): string {
  const w = width.trim();
  const a = aspect.trim();
  const d = diameter.trim();
  if (!w || !a || !d) return '';
  const core = `${w}/${a}R${d}`;
  const prefix = tireTypeNotationPrefix(type ?? '');
  return prefix ? `${prefix}${core}` : core;
}

export function formatCapturedTireSize(
  notes: Pick<VehicleNotesFormState, 'tireType' | 'tireSectionWidthMm' | 'tireAspectRatio' | 'tireWheelDiameterIn'>
): string {
  return formatTireSizeFromParts(
    notes.tireSectionWidthMm,
    notes.tireAspectRatio,
    notes.tireWheelDiameterIn,
    notes.tireType
  );
}

export function formatOemCapturedTireSize(
  notes: Pick<VehicleNotesFormState, 'oemTireSectionWidthMm' | 'oemTireAspectRatio' | 'oemTireWheelDiameterIn'>
): string {
  return formatTireSizeFromParts(
    notes.oemTireSectionWidthMm,
    notes.oemTireAspectRatio,
    notes.oemTireWheelDiameterIn
  );
}

export function labelTireTypeForReview(tireType: string): string {
  return tireTypeToPayloadValue(tireType) || '—';
}

export function labelFluidLevelForReview(level: string): string {
  const match = FLUID_LEVEL_OPTIONS.find((opt) => opt.value === level);
  return match?.label ?? (level.trim() || '—');
}

export function labelLeakDripForReview(value: string): string {
  const match = LEAK_DRIP_OPTIONS.find((opt) => opt.value === value);
  return match?.label ?? (value.trim() || '—');
}

export function vehicleNotesToPayload(notes: VehicleNotesFormState): VehicleNotesPayload {
  const isModified = notes.isVehicleModified === 'yes';

  return {
    parts_labor_warranty_months: notes.partsLaborWarrantyMonths.trim(),
    parts_labor_warranty_miles: notes.partsLaborWarrantyMiles.trim(),
    is_vehicle_modified: notes.isVehicleModified.trim(),
    tire_type: isModified ? tireTypeToPayloadValue(notes.tireType) : '',
    tire_section_width_mm: isModified ? notes.tireSectionWidthMm.trim() : '',
    tire_aspect_ratio: isModified ? notes.tireAspectRatio.trim() : '',
    tire_wheel_diameter_in: isModified ? notes.tireWheelDiameterIn.trim() : '',
    oem_tire_section_width_mm: isModified ? notes.oemTireSectionWidthMm.trim() : '',
    oem_tire_aspect_ratio: isModified ? notes.oemTireAspectRatio.trim() : '',
    oem_tire_wheel_diameter_in: isModified ? notes.oemTireWheelDiameterIn.trim() : '',
    has_lift_kit: isModified ? notes.hasLiftKit.trim() : '',
    lift_amount_inches: isModified && notes.hasLiftKit === 'yes' ? notes.liftAmountInches.trim() : '',
    has_engine_modifications: isModified ? notes.hasEngineModifications.trim() : '',
    engine_modification_description:
      isModified && notes.hasEngineModifications === 'yes'
        ? notes.engineModificationDescription.trim()
        : '',
    coolant_level: notes.coolantLevel.trim(),
    coolant_condition: notes.coolantCondition.trim(),
    engine_oil_level: notes.engineOilLevel.trim(),
    engine_oil_condition: notes.engineOilCondition.trim(),
    transmission_fluid_level: notes.transmissionFluidLevel.trim(),
    transmission_fluid_condition: notes.transmissionFluidCondition.trim(),
    has_leaks: notes.hasLeaks.trim(),
    leak_location: notes.hasLeaks === 'yes' ? notes.leakLocation.trim() : '',
    leak_drip_condition: notes.hasLeaks === 'yes' ? notes.leakDripCondition.trim() : '',
    has_warning_light: notes.hasWarningLight.trim(),
    codes_and_definitions: notes.hasWarningLight === 'yes' ? notes.codesAndDefinitions.trim() : '',
  };
}

export function sanitizeTireNumericInput(value: string): string {
  return value.replace(/\D/g, '');
}

const PARTS_LABOR_WARRANTY_SPECIAL = /^(?:n\/a|na|unknown)$/i;

export const PARTS_LABOR_WARRANTY_MONTHS_ERROR =
  'Months is required. Enter a number, N/A or Unknown.';
export const PARTS_LABOR_WARRANTY_MILES_ERROR =
  'Miles is required. Enter a number, N/A or Unknown.';

/** Keeps letters, digits, and "/" so values like 12, N/A, and Unknown can be entered. */
export function sanitizePartsLaborWarrantyInput(raw: string): string {
  return raw.replace(/[^a-zA-Z0-9/]/g, '');
}

/** Whole number greater than 0, or N/A / Unknown (any casing). */
export function isPartsLaborWarrantyValueValid(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed) return false;
  if (PARTS_LABOR_WARRANTY_SPECIAL.test(trimmed)) return true;
  if (!/^\d+$/u.test(trimmed)) return false;
  const n = Number(trimmed);
  return Number.isSafeInteger(n) && n > 0;
}

export function partsLaborWarrantyFieldError(
  value: string,
  field: 'months' | 'miles'
): string | undefined {
  if (isPartsLaborWarrantyValueValid(value)) return undefined;
  return field === 'months' ? PARTS_LABOR_WARRANTY_MONTHS_ERROR : PARTS_LABOR_WARRANTY_MILES_ERROR;
}

export function isPartsLaborWarrantyComplete(
  notes: Pick<VehicleNotesFormState, 'partsLaborWarrantyMonths' | 'partsLaborWarrantyMiles'>
): boolean {
  return (
    isPartsLaborWarrantyValueValid(notes.partsLaborWarrantyMonths) &&
    isPartsLaborWarrantyValueValid(notes.partsLaborWarrantyMiles)
  );
}

export function sanitizeLiftAmountInput(raw: string): string {
  let out = '';
  let hasDot = false;
  for (const ch of raw) {
    if (ch >= '0' && ch <= '9') {
      out += ch;
    } else if (ch === '.' && !hasDot) {
      out += ch;
      hasDot = true;
    }
  }
  return out;
}
